import { PlusOutlined } from '@ant-design/icons'
import { Flex, Divider, Button } from 'antd'
import { useMemo, useState } from 'react'

import { usePersonsServiceGetInstitutionPersonsPatientsListEndpoint } from 'src/api/queries'
import { Gender, Permissions } from 'src/api/requests'
import { PageBreadcrumbs, SearchBar, RangePicker, Select } from 'src/components'
import {
  NewEpidemiologyRequests,
  PersonsTable,
  SearchPersonModal
} from 'src/features/persons/components'
import { useTablePagination, useUser } from 'src/hooks'
import { disabledFutureDate, legalAgePickerValue } from 'src/libs'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

export const PatientsListPage = () => {
  const { t } = useLocales()

  const pagination = useTablePagination()

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState<string>()
  const [gender, setGender] = useState<string>()
  const [dateTo, setDateTo] = useState('')
  const [dateFrom, setDateFrom] = useState('')

  const { hasPermission, userData } = useUser()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    simeLegalEntityId: String(userData?.currentLegalEntityId),
    simeRoleId: String(userData?.currentRoleId),
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!gender && {
      gender: gender as Gender
    }),
    ...(!!dateFrom &&
      !!dateTo && {
        birthDateFrom: dateFrom,
        birthDateTo: dateTo
      })
  }

  const { data, isLoading } =
    usePersonsServiceGetInstitutionPersonsPatientsListEndpoint(queryParams)

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onGenderChange = (val: string) => {
    setGender(val)
    resetPagination(pagination)
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  const genderOptions = useMemo(
    () => [
      {
        value: 'male',
        label: t('form.male')
      },
      {
        value: 'female',
        label: t('form.female')
      },
      {
        value: 'unknown',
        label: t('form.unknown')
      }
    ],
    [t]
  )

  return (
    <PageBreadcrumbs
      title={t('menu.patients')}
      items={[
        {
          title: t('menu.patients')
        }
      ]}
    >
      <Flex justify="space-between" gap={16} wrap="wrap">
        <Flex gap={16} wrap="wrap">
          <SearchBar
            maxWidth={330}
            onSearch={onSearch}
            placeholder={t('persons.idnp_name_surname_sime_code')}
          />

          <Select
            options={genderOptions}
            onChange={onGenderChange}
            placeholder={t('form.gender')}
            allowClear
          />

          <RangePicker
            setDateFrom={setDateFrom}
            setDateTo={setDateTo}
            resetPagination={() => resetPagination(pagination)}
            disabledDate={disabledFutureDate}
            defaultPickerValue={legalAgePickerValue}
            placement="bottomRight"
            placeholder={[
              t('persons.from_date_of_birth'),
              t('persons.to_date_of_birth')
            ]}
          />
        </Flex>

        {hasPermission(Permissions.PERSONS_PATIENT_VIEW_INSTITUTION) && (
          <Button onClick={onToggle} icon={<PlusOutlined />} type="primary">
            {t('persons.add_new_person')}
          </Button>
        )}
      </Flex>

      <Divider />

      {hasPermission(
        Permissions.TREATMENT_EPIDEMIOLOGY_CASE_VIEW_REQUEST_ALL
      ) && <NewEpidemiologyRequests />}

      <PersonsTable
        isPatient
        dataSource={data?.data}
        loading={isLoading}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {isOpen && <SearchPersonModal onCancel={onToggle} />}
    </PageBreadcrumbs>
  )
}
